// Constants
import { TelemetryPlatform } from "@ms/uno-constants/lib/local/TelemetryConstants";
import { WebQueryParams } from "@ms/uno-constants/lib/local/WebConstants";
// Model
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { generateDeepLinkFromRoute, generateRouteFromDeepLink, parseRouteDescriptor, removePathPrefixIfPresent } from "@ms/uno-routing";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { PageTimingKey, setPageTiming } from "@ms/uno-utilities/lib/local/PASUtilities";
import { generateRouteFromLegacyDeepLink, generateRouteFromTransformedLegacyDeepLink } from "../utils/LegacyWebDeepLinkUtils";
import { classifyUnresolvedDeepLink, getLocale, UnresolvedDeepLinkShape } from "../utils/WebAdaptorUtils";
import { collectScriptCacheMetrics } from "../utils/WebPlatformPerformanceMetrics";
/**
 * Web Class implementing IHostAdaptor interface to provide interaction with host platform
 */ export class WebAdaptor {
    /**
     * Sets the loggers to be used by the adaptor
     */ setLoggers(loggers) {
        this.loggers = loggers;
    }
    async getContext() {
        const account = this.msalHelperProvider().getActiveAccount();
        if (account == null) {
        // TEMP[UnoWeb] - Gracefully error out
        }
        // TEMP[UnoWeb] - Remove hard coded values and replace with actual values
        const context = {
            locale: getLocale(),
            theme: this.themeQueryEvaluator(),
            sessionId: this.getCurrentSessionId(),
            userId: account?.userId ?? null,
            displayName: account?.displayName ?? null,
            userUpn: account?.loginHint ?? null,
            tenantId: account?.tenantId ?? null,
            ringId: null,
            platform: TelemetryPlatform.Web,
            isGuestUser: account?.isGuest ?? false,
            isTenantAdmin: false,
            isBackgroundLoad: false
        };
        this.tenantId = context.tenantId;
        return context;
    }
    async getAccessToken(resource, claims, silent) {
        const scopes = [
            `${resource}/.default`
        ];
        return this.getAccessTokenForScopes(scopes, claims, silent);
    }
    async getAccessTokenForScopes(scopes, claims, silent) {
        try {
            return await this.msalHelperProvider().getAccessToken(scopes, claims, silent);
        } catch (error) {
            this.loggers?.traceLogger.logTrace(0x1e19e75d /* tag_4g433 */ , TraceLevel.Warning, `[WebAdaptor] Error fetching the access token [Scopes=${scopes?.join(",") ?? ""}][HasClaims=${!!claims}][IsSilent=${!!silent}]`);
            return Promise.reject(error);
        }
    }
    notifyLoaded() {
    // TODO: Implement logic to notify the host that the app has loaded
    }
    notifyRenderSuccess() {
        setPageTiming(PageTimingKey.eupl, performance.now());
    }
    notifyRenderFailure(failure) {
    // TODO: Implement logic to notify the host about the app initialization failure
    }
    notifyExpectedFailure(failure) {
    // TODO: Implement logic to notify the host about the expected app initialization failure
    }
    openLinkInHost(relativeUrl) {
    // TODO: Implement logic to open a link in the host application
    }
    openExternalAppLinkInHost(externalUrl) {
        safelyOpenExternalUrl(externalUrl);
    }
    openFilePreviewInHost(fileUrl, fileAlias, route, sharePointBaseUrl, sharePointListItemUniqueId) {
        safelyOpenExternalUrl(fileUrl, this.loggers?.traceLogger);
    }
    async getInitialRoute() {
        // Let's see if there's initial route from the browser URL path
        // We will attempt to parse as UNO DeepLink Schema first
        // Then we will attempt to parse as UNO Route path
        let { pathname } = window.location;
        // Strip the active web URL prefix detected at bootstrap
        pathname = removePathPrefixIfPresent(pathname, this.appConfig().runtimeConfig.activeWebUrlPrefix);
        const { search, hash } = window.location;
        const urlSearchParams = new URLSearchParams(search ?? "");
        const urlParamsString = urlSearchParams.toString();
        let path = pathname;
        path = urlParamsString ? `${path}?${urlParamsString}` : path;
        path = hash ? `${path}${hash}` : path;
        const routeFromDeepLink = generateRouteFromDeepLink(path, this.loggers, (deepLink, loggers)=>generateRouteFromLegacyDeepLink(deepLink, loggers, this.appConfig().flights.EnableMeetingPlanExpiration));
        if (routeFromDeepLink) {
            this.loggers?.traceLogger.logTrace(0x1e19e75c /* tag_4g432 */ , TraceLevel.Info, `getInitialRoute - Route from DeepLink`);
            return routeFromDeepLink;
        }
        const routeFromPath = parseRouteDescriptor(path, null);
        if (routeFromPath) {
            this.loggers?.traceLogger.logTrace(0x1e19e75b /* tag_4g431 */ , TraceLevel.Info, `getInitialRoute - Route from Path`);
            return routeFromPath;
        }
        const routeFromTransformedLegacyParse = generateRouteFromTransformedLegacyDeepLink(pathname, hash, this.loggers);
        if (routeFromTransformedLegacyParse) {
            this.loggers?.traceLogger.logTrace(0x1e19e75a /* tag_4g430 */ , TraceLevel.Info, `getInitialRoute - Route from Transformed Legacy Parse`);
            return routeFromTransformedLegacyParse;
        }
        // Every parser declined. Returning null here makes the app fall back to the persisted or
        // default route, so a followed link silently resolves to unrelated content. Record the link
        // shape (never the path, which carries identifiers) to size that failure.
        const unresolvedShape = classifyUnresolvedDeepLink(pathname);
        if (unresolvedShape !== UnresolvedDeepLinkShape.None) {
            this.loggers?.traceLogger.logTrace(0x1d79b20a /* tag_341ik */ , TraceLevel.Warning, `getInitialRoute - No route resolved from URL [Shape=${unresolvedShape}][HasQuery=${!!urlParamsString}][HasHash=${!!hash}]`);
        }
        return null;
    }
    generateDeepLink(route) {
        const deepLink = generateDeepLinkFromRoute(route, this.loggers);
        if (!deepLink) {
            this.loggers?.traceLogger.logTrace(0x1e19e759 /* tag_4g43z */ , TraceLevel.Error, `generateDeepLink - Unable to generate DeepLink from Route`);
            return null;
        }
        let deepLinkUrl = null;
        try {
            deepLinkUrl = new URL(`${window.location.origin}/${this.appConfig().runtimeConfig.activeWebUrlPrefix}${deepLink}`);
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e19e758 /* tag_4g43y */ , TraceLevel.Error, `generateDeepLink - Error instantiating URL object`);
            return null;
        }
        const defaultQueryParameters = this.getDefaultQueryParameters() ?? {};
        for(const key in defaultQueryParameters){
            if (!deepLinkUrl.searchParams.has(key)) {
                deepLinkUrl.searchParams.set(key, defaultQueryParameters[key]);
            }
        }
        return deepLinkUrl.toString();
    }
    getDefaultQueryParameters() {
        return this.tenantId ? {
            [WebQueryParams.TenantId]: this.tenantId
        } : null;
    }
    registerThemeChangeHandler(handler) {
        if (window.matchMedia) {
            const listener = ()=>{
                const theme = this.themeQueryEvaluator();
                handler(theme);
            };
            this.hcQuery.addEventListener?.("change", listener);
            this.darkQuery.addEventListener?.("change", listener);
        }
    }
    canSetDocumentTitle() {
        return true;
    }
    /**
     * Collect system performance metrics including cache hit/miss ratio for static assets during boot completion
     */ collectSystemPerformanceMetrics(bundlesToPrecache) {
        return collectScriptCacheMetrics(bundlesToPrecache);
    }
    /**
     * Returns the sessionId to use for telemetry
     * Note: This changes based on whether there is an active account or not
     */ getCurrentSessionId() {
        // First check if the active account situation changed (and update if necessary)
        if (this.currentSessionIdComputedWhenActiveAccount !== this.msalHelperProvider().hasActiveAccount()) {
            this.currentSessionIdComputedWhenActiveAccount = this.msalHelperProvider().hasActiveAccount();
            this.currentSessionId = generateGuid();
        }
        return this.currentSessionId;
    }
    constructor(appConfig, msalHelperProvider){
        this.appConfig = appConfig;
        this.msalHelperProvider = msalHelperProvider;
        this.loggers = null;
        this.tenantId = null;
        // High Contrast and Dark Mode media queries
        this.hcQuery = matchMedia("(forced-colors: active)");
        this.darkQuery = matchMedia("(prefers-color-scheme: dark)");
        this.themeQueryEvaluator = ()=>{
            if (this.hcQuery.matches) {
                return PlannerTheme.HighContrast;
            } else if (this.darkQuery.matches) {
                return PlannerTheme.Dark;
            } else {
                return PlannerTheme.Light;
            }
        };
        if (msalHelperProvider().hasActiveAccount()) {
            this.currentSessionIdComputedWhenActiveAccount = true;
            this.currentSessionId = generateGuid();
        } else {
            this.currentSessionIdComputedWhenActiveAccount = false;
            this.currentSessionId = this.appConfig().sessionMetaData.sessionId;
        }
    }
}
